import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import Global from '../../../../../store/global-selectors';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { LineChart, Line, Label, Tooltip, Legend, CartesianGrid, XAxis, YAxis, ResponsiveContainer } from 'recharts';

import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import Colors from '../utilities/colors';
import { MAIN_GRAPH_HEIGHT, Y_AXIS_TICK_LENGTH } from '../utilities/constants';
import { getInterval } from '../../helper';
import NullStateOverlay from '../../null-state-overlay';
import { emptyArray } from 'ui-components/default-props/empty';
import CustomTooltip from '../custom-tooltip';
import CustomTooltipNumItemsOverTime from '../custom-tooltip/custom-tooltip-num-items-over-time';
import { useUserDateFormat } from '../../../../../hooks/use-datetime-formats';

import messages from './messages';
import style from './style.module.less';
import analyticsStyle from '../../../analytics-style.module.less';

const NumItemsSoldOverTimeGraph = ({ data = emptyArray, shouldShowNullOverlay = false }) => {
  const intl = useIntl();
  const defaultCurrency = useSelector(Global.selectSellerDefaultCurrency) || 'USD';
  const { formatUserDate } = useUserDateFormat();
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const formattedData = useMemo(() => {
    return data.map((datum) => ({
      ...datum,
      revenue: datum.amount,
      count: datum.itemCount,
    }));
  }, [data]);

  const messageMap = useMemo(
    () => ({
      count: intl.formatMessage(messages.itemsSold),
      revenue: intl.formatMessage(messages.sales, {
        currency: defaultCurrency,
      }),
    }),
    [defaultCurrency, intl],
  );

  const formatXTicks = useCallback(
    (date) => {
      return formatUserDate(date, false);
    },
    [formatUserDate],
  );

  const interval = useMemo(() => getInterval(formattedData), [formattedData]);

  const formatRightSideYTicks = useCallback(
    (val) => {
      return intl.formatNumber(val, {
        style: 'currency',
        currency: defaultCurrency,
      });
    },
    [defaultCurrency, intl],
  );

  const formatLegend = useCallback(
    (value) => {
      return <span>{messageMap[value]}</span>;
    },
    [messageMap],
  );

  return (
    <>
      {isShipStationAnalyticsFeatureEnabled && (
        <Text className={analyticsStyle['analytics-section-title']} textType={TextType.H4} value={messages.salesandCustomersReportTitle} />
      )}
      {shouldShowNullOverlay && <NullStateOverlay />}
      <ResponsiveContainer height={MAIN_GRAPH_HEIGHT} width='100%'>
        <LineChart
          data={formattedData}
          height={300}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          style={{
            fontSize: 12,
          }}
          width={500}
        >
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='interval' interval={interval} tickFormatter={formatXTicks} tickLine={false} />
          <YAxis dataKey='count' tickLine={false} tickSize={Y_AXIS_TICK_LENGTH} yAxisId='left'>
            <Label
              angle={-90}
              className={classnames(style['yAxis-label'], {
                [analyticsStyle['analytics-yAxis-label']]: isShipStationAnalyticsFeatureEnabled,
              })}
              position='insideLeft'
              value={intl.formatMessage(messages.yAxisLabel)}
            />
          </YAxis>
          <YAxis
            dataKey='revenue'
            orientation='right'
            tickFormatter={formatRightSideYTicks}
            tickLine={false}
            tickSize={Y_AXIS_TICK_LENGTH}
            width={100}
            yAxisId='right'
          >
            <Label
              angle={-90}
              className={classnames(style['yAxis-label'], {
                [analyticsStyle['analytics-yAxis-label']]: isShipStationAnalyticsFeatureEnabled,
              })}
              position='insideRight'
              value={intl.formatMessage(messages.revenueWithCurrency, {
                currency: defaultCurrency,
              })}
            />
          </YAxis>
          <Legend
            formatter={formatLegend}
            wrapperStyle={{
              display: 'flex',
              justifyContent: 'center',
            }}
          />
          <Tooltip
            content={
              <CustomTooltip>
                <CustomTooltipNumItemsOverTime />
              </CustomTooltip>
            }
          />
          <Line dataKey='count' stroke={Colors.BAR_COLOR_PURPLE} type='monotone' yAxisId='left' />
          <Line dataKey='revenue' stroke={Colors.BAR_COLOR_BLUE} type='monotone' yAxisId='right' />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

NumItemsSoldOverTimeGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      interval: PropTypes.string,
      shippedCount: PropTypes.number,
      amount: PropTypes.number,
      itemCount: PropTypes.number,
    }),
  ),
  shouldShowNullOverlay: PropTypes.bool,
};

export default NumItemsSoldOverTimeGraph;
